import { Alert, Button, Group, Tooltip } from "@mantine/core";
import type { FC } from "react";

import type { CurrentNotice } from "src/api/Tmd.dtos";
import Icon from "src/components/common/Icon";
import RichTextContent from "src/components/common/RichTextContent";

import { getNoticeSeverityPresentation } from "./noticeSeverityPresentation";

type NoticeAlertProps = {
    notice: CurrentNotice;
    onAcknowledge: (noticeId: number) => Promise<void>;
    onDismiss: (noticeId: number) => void;
    banner?: boolean;
};

/**
 * Presents a notice with temporary dismissal and optional acknowledgement controls.
 * Postconditions: Dismiss is always offered; acknowledgement is offered only when dismissible.
 * Callbacks receive the notice ID. Acknowledgement is fire-and-forget without a local busy state.
 * Assumptions: The supplied callbacks own state updates, persistence and failure reporting.
 */
const NoticeAlert: FC<NoticeAlertProps> = ({
    notice,
    onAcknowledge,
    onDismiss,
    banner = false
}) => {
    const severity = getNoticeSeverityPresentation(notice.severity);
    return (
        <Alert
            color={severity.color}
            icon={<Icon icon={severity.icon} />}
            title={notice.title}
            radius={banner ? 0 : undefined}
            data-notice-banner={banner || undefined}
        >
            <RichTextContent
                content={notice.message}
                fz="sm"
            />
            <Group
                justify="flex-end"
                mt="sm"
            >
                <Tooltip label="Dismiss this notice temporarily. It will appear again after you reload the application.">
                    <Button
                        size="xs"
                        variant="subtle"
                        color={severity.color}
                        onClick={() => onDismiss(notice.id)}
                    >
                        Dismiss
                    </Button>
                </Tooltip>
                {notice.dismissible && (
                    <Tooltip label="Acknowledge this notice to dismiss it and prevent it from appearing again.">
                        <Button
                            size="xs"
                            variant="light"
                            color={severity.color}
                            onClick={() => void onAcknowledge(notice.id)}
                        >
                            Acknowledge
                        </Button>
                    </Tooltip>
                )}
            </Group>
        </Alert>
    );
};

export default NoticeAlert;
